import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TokenStorage } from '../utils/token-storage';
import { useAdminRefreshToken } from './useGraphQL';
import { useFirebaseMessaging } from './useFirebaseMessaging';
import { clearRegisteredTokens } from '../utils/fcm-token-manager';

interface AdminUser {
  id: string;
  email: string;
  firstname: string;
  lastname: string;
  role_id?: string;
  permissions: string[];
  is_super_admin: boolean;
  is_active: boolean;
  last_login_at?: string;
  last_login_ip?: string;
  phone_number?: string;
  avatar_url?: string;
  notes?: string;
  tenant: string;
  doctor_id?: string;
  created_at: string;
  updated_at: string;
  userRole?: {
    id: string;
    name: string;
    description: string;
    is_system_role: boolean;
    is_active: boolean;
    permissions: Array<{
      role_id: string;
      permission: string;
    }>;
  };
  
  // Helper methods
  isNurse(): boolean;
  isDoctor(): boolean;
  canViewAppointments(): boolean;
  canManageAppointments(): boolean;
}

interface TokenData {
  access_token: string;
  refresh_token: string;
  expires_at?: string;
}

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [adminRefreshToken] = useAdminRefreshToken();
  const { requestPermission: requestFCMPermission, registerFCMToken, revokePermission: revokeFCMPermission } = useFirebaseMessaging();

  // Check token expiration and auto logout
  const checkTokenExpiration = useCallback(() => {
    if (TokenStorage.isAccessTokenExpired()) {
      console.log('Token expired, logging out...');
      logout();
      return false;
    }
    return true;
  }, []);

  // Auto logout when token expires
  useEffect(() => {
    const checkExpiration = () => {
      if (isAuthenticated && !checkTokenExpiration()) {
        return;
      }
    };

    // Check immediately
    checkExpiration();

    // Set up interval to check every minute
    const interval = setInterval(checkExpiration, 60000);

    return () => clearInterval(interval);
  }, [isAuthenticated, checkTokenExpiration]);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      const token = TokenStorage.getAccessToken();
      const userData = localStorage.getItem('adminUser');
      
      if (token && userData) {
        try {
          const parsedUser = JSON.parse(userData);
          
          // Check if token is expired
          if (TokenStorage.isAccessTokenExpired()) {
            console.log('Stored token is expired, clearing...');
            TokenStorage.clearTokens();
            setUser(null);
            setIsAuthenticated(false);
          } else {
            // Add helper methods to the user object
            const userWithMethods = {
              ...parsedUser,
              isNurse: () => parsedUser.userRole?.name?.toLowerCase().includes('nurse') || parsedUser.is_super_admin,
              isDoctor: () => parsedUser.userRole?.name?.toLowerCase().includes('doctor') || parsedUser.is_super_admin,
              canViewAppointments: () => {
                return parsedUser.permissions.includes('view_appointments') ||
                       parsedUser.is_super_admin;
              },
              canManageAppointments: () => {
                return parsedUser.permissions.includes('update_appointments') ||
                       parsedUser.is_super_admin;
              }
            };
            setUser(userWithMethods);
            setIsAuthenticated(true);
            
            // FCM registration is now manual - user must click approve button
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
          TokenStorage.clearTokens();
        }
      }
      
      setLoading(false);
    };

    initializeAuth().catch(console.error);
  }, []); // Remove requestFCMPermission from dependencies to prevent infinite re-renders

  const login = useCallback(async (tokenData: TokenData, userData: AdminUser) => {
    // Add helper methods to the user object
    const userWithMethods = {
      ...userData,
      isNurse: () => userData.userRole?.name?.toLowerCase().includes('nurse') || userData.is_super_admin,
      isDoctor: () => userData.userRole?.name?.toLowerCase().includes('doctor') || userData.is_super_admin,
      canViewAppointments: () => {
        return userData.permissions.includes('view_appointments') ||
               userData.is_super_admin;
      },
      canManageAppointments: () => {
        return userData.permissions.includes('update_appointments') ||
               userData.is_super_admin;
      }
    };
    
    TokenStorage.setTokens(tokenData);
    localStorage.setItem('adminUser', JSON.stringify(userWithMethods));
    setUser(userWithMethods);
    setIsAuthenticated(true);
    
    // FCM registration is now manual - user must click approve button
    
    console.log('Login successful:', userWithMethods);
    window.location.href = '/appointments';
  }, [navigate, requestFCMPermission]);

  const logout = useCallback(async () => {
    // Revoke FCM permission before logout
    try {
      await revokeFCMPermission();
    } catch (error) {
      console.error('Failed to revoke FCM permission:', error);
    }
    
    // Clear registered FCM tokens cache
    clearRegisteredTokens();
    
    TokenStorage.clearTokens();
    setUser(null);
    setIsAuthenticated(false);
    
    console.log('Logout successful');
    window.location.href = '/login';
  }, [navigate, revokeFCMPermission]);

  const refreshToken = useCallback(async (refreshTokenValue: string) => {
    try {
      const response = await adminRefreshToken({
        variables: {
          refreshToken: refreshTokenValue
        }
      });

      if (response.data?.adminRefreshToken) {
        const { adminUser, accessToken, refreshToken: newRefreshToken } = response.data.adminRefreshToken;
        
        // Calculate new token expiration (1 hour from now)
        const expiresAt = new Date(Date.now() + 60 * 60 * 1000).toISOString();
        
        // Update tokens
        TokenStorage.setTokens({
          access_token: accessToken,
          refresh_token: newRefreshToken,
          expires_at: expiresAt
        });
        
        // Update user data with proper permissions
        const userWithPermissions = {
          ...adminUser,
          permissions: adminUser.permissions || [],
          is_super_admin: adminUser.is_super_admin || false,
          role: adminUser.role || 'ADMIN'
        };
        
        // Add helper methods
        const userWithMethods = {
          ...userWithPermissions,
          isNurse: () => userWithPermissions.userRole?.name?.toLowerCase().includes('nurse') || userWithPermissions.is_super_admin,
          isDoctor: () => userWithPermissions.userRole?.name?.toLowerCase().includes('doctor') || userWithPermissions.is_super_admin,
          canViewAppointments: () => {
            return userWithPermissions.permissions.includes('view_appointments') ||
                   userWithPermissions.is_super_admin;
          },
          canManageAppointments: () => {
            return userWithPermissions.permissions.includes('update_appointments') ||
                   userWithPermissions.is_super_admin;
          }
        };
        
        localStorage.setItem('adminUser', JSON.stringify(userWithMethods));
        setUser(userWithMethods);
        
        console.log('Token refreshed successfully');
      } else {
        console.log('Refresh token failed, logging out...');
        logout();
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      logout();
    }
  }, [adminRefreshToken, logout]);

  // Check if token will expire soon and attempt refresh
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkTokenRefresh = () => {
      if (TokenStorage.willExpireSoon(10)) { // 10 minutes before expiration
        console.log('Token expiring soon, attempting refresh...');
        const refreshTokenValue = TokenStorage.getRefreshToken();
        if (refreshTokenValue) {
          refreshToken(refreshTokenValue);
        }
      }
    };

    // Check every 5 minutes
    const interval = setInterval(checkTokenRefresh, 300000);
    checkTokenRefresh(); // Check immediately

    return () => clearInterval(interval);
  }, [isAuthenticated, refreshToken]);

  return {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    refreshToken,
    checkTokenExpiration,
    registerFCMToken
  };
}; 